import {
  api,
  type DashboardAccessTokenItem,
  type DashboardIssueItem,
  type DashboardSkillItem,
  type DashboardSlice,
  type DashboardSubmissionItem,
} from './api-client';
import { escapeHtml } from './html';
import { issueStatusBadge, submissionStatusBadge } from './status-ui';
import type { ActivityDay } from '../types/api';

const HEATMAP_COLORS = ['#27272a', '#0e4429', '#006d32', '#26a641', '#39d353'];

function countToColor(count: number): string {
  if (count === 0) return HEATMAP_COLORS[0];
  if (count <= 2) return HEATMAP_COLORS[1];
  if (count <= 5) return HEATMAP_COLORS[2];
  if (count <= 9) return HEATMAP_COLORS[3];
  return HEATMAP_COLORS[4];
}

function appendHeatmapSpacer(container: HTMLElement): void {
  const spacer = document.createElement('span');
  spacer.style.cssText = 'min-width:13px;display:inline-block';
  container.appendChild(spacer);
}

function renderHeatmap(days: readonly ActivityDay[]): void {
  const grid = document.getElementById('dash-heatmap-grid');
  const months = document.getElementById('dash-heatmap-months');
  const tooltip = document.getElementById('dash-heatmap-tooltip');
  if (!grid) return;

  const cells: Array<{ date: string | null; count: number }> = [];
  const firstDate = days[0]?.date;
  for (let i = 0; i < (firstDate ? new Date(firstDate + 'T00:00:00').getDay() : 0); i++) {
    cells.push({ date: null, count: 0 });
  }
  cells.push(...days.map(({ date, count }) => ({ date, count })));

  grid.innerHTML = '';
  cells.forEach(({ date, count }) => {
    const cell = document.createElement('div');
    cell.style.cssText = 'width:11px;height:11px;border-radius:2px;background:' + countToColor(count) + ';cursor:' + (date ? 'pointer' : 'default');
    if (date && tooltip) {
      const positionTooltip = (event: MouseEvent): void => {
        tooltip.style.left = (event.clientX + 12) + 'px';
        tooltip.style.top = (event.clientY - 30) + 'px';
      };
      cell.addEventListener('mouseenter', (event) => {
        tooltip.textContent = count + ' contribution' + (count !== 1 ? 's' : '') + ' on ' + date;
        tooltip.style.display = 'block';
        positionTooltip(event);
      });
      cell.addEventListener('mousemove', positionTooltip);
      cell.addEventListener('mouseleave', () => { tooltip.style.display = 'none'; });
    }
    grid.appendChild(cell);
  });

  if (!months) return;
  months.innerHTML = '';
  let lastMonth = -1;
  for (let column = 0; column < Math.ceil(cells.length / 7); column++) {
    const date = cells[column * 7]?.date;
    if (!date) {
      appendHeatmapSpacer(months);
      continue;
    }
    const month = new Date(date + 'T00:00:00').getMonth();
    if (month === lastMonth) {
      appendHeatmapSpacer(months);
      continue;
    }
    const label = document.createElement('span');
    label.textContent = new Date(date + 'T00:00:00').toLocaleString('en', { month: 'short' });
    label.style.cssText = 'font-size:11px;color:#71717a;white-space:nowrap;min-width:0';
    months.appendChild(label);
    lastMonth = month;
  }
}

export async function loadDashboardHeatmap(username: string): Promise<void> {
  try {
    const result = await api.users.getActivity(username);
    if (!result.success) return;
    const total = document.getElementById('dash-heatmap-total');
    if (total) total.textContent = String(result.data.total);
    const heatmap = document.getElementById('dashboard-heatmap');
    if (heatmap) heatmap.style.display = '';
    renderHeatmap(result.data.days);
  } catch (error) {
    console.error('Failed to load heatmap:', error);
  }
}

function renderDashboardList<T>(
  containerId: string,
  items: readonly T[] | undefined,
  emptyIcon: string,
  emptyTitle: string,
  renderItem: (item: T) => string,
): void {
  const list = document.getElementById(containerId);
  if (!list) return;
  list.innerHTML = items?.length
    ? items.map(renderItem).join('')
    : window.emptyStateHtml(emptyIcon, emptyTitle, 'text-center py-8');
}

export function renderDashboardIssues(issues: DashboardSlice<DashboardIssueItem>): void {
  renderDashboardList('my-issues-list', issues.items, 'fa-inbox', 'No issues yet', issue =>
    '<a href="/issues/' + issue.id + '" class="card flex items-center gap-4 p-4 group">' +
      '<div class="flex-1 min-w-0"><div class="flex items-center gap-2 mb-1">' +
        '<h3 class="font-medium text-zinc-200 text-sm group-hover:text-brand-400 transition-colors truncate">' + escapeHtml(issue.title) + '</h3>' +
        issueStatusBadge(issue.status) +
      '</div><div class="text-xs text-zinc-600">' + issue.vote_count + ' want this &middot; ' + (issue.created_at ? window.timeAgo(issue.created_at) : 'unknown') + '</div></div>' +
      '<i class="fas fa-chevron-right text-zinc-700"></i>' +
    '</a>');
}

export function renderDashboardSubmissions(submissions: DashboardSlice<DashboardSubmissionItem>): void {
  renderDashboardList('my-submissions-list', submissions.items, 'fa-code-branch', 'No submissions yet', submission => {
    const adopted = submission.skill_id
      ? '<span class="status-badge ml-1" style="background:rgba(30,58,138,0.3);color:#93c5fd;border-color:rgba(59,130,246,0.3)"><i class="fas fa-check-circle"></i>Adopted</span>'
      : '';
    return '<a href="/issues/' + submission.issue_id + '" class="card flex items-center gap-4 p-4 group">' +
      '<div class="flex-1 min-w-0"><div class="flex items-center gap-2 mb-1">' +
        '<h3 class="font-medium text-zinc-200 text-sm group-hover:text-brand-400 transition-colors truncate">' + escapeHtml(submission.issue_title || 'Issue #' + submission.issue_id) + '</h3>' +
        submissionStatusBadge(submission.status) + adopted +
      '</div><div class="text-xs text-zinc-600">Submission #' + submission.id + ' &middot; ' + (submission.created_at ? window.timeAgo(submission.created_at) : 'unknown') + '</div></div>' +
      '<i class="fas fa-chevron-right text-zinc-700"></i>' +
    '</a>';
  });
}

export function renderDashboardSkills(skills: DashboardSlice<DashboardSkillItem>): void {
  renderDashboardList('my-skills-list', skills.items, 'fa-puzzle-piece', 'No published skills yet', skill =>
    '<a href="/skills/' + skill.id + '" class="card flex items-center gap-4 p-4 group">' +
      '<div class="w-10 h-10 bg-surface-2 rounded-xl flex items-center justify-center"><i class="fas fa-puzzle-piece text-brand-400"></i></div>' +
      '<div class="flex-1 min-w-0"><div class="flex items-center gap-2 mb-1">' +
        '<h3 class="font-medium text-zinc-200 text-sm group-hover:text-brand-400 transition-colors truncate">' + escapeHtml(skill.name) + '</h3>' +
        (skill.version ? '<span class="text-xs text-zinc-600">v' + escapeHtml(skill.version) + '</span>' : '') +
      '</div><div class="text-xs text-zinc-600">' + skill.download_count + ' downloads</div></div>' +
      '<i class="fas fa-chevron-right text-zinc-700"></i>' +
    '</a>');
}

export function renderDashboardAccessTokens(tokens: { items: DashboardAccessTokenItem[] }): void {
  renderDashboardList('access-tokens-list', tokens.items, 'fa-key', 'No access tokens yet', token => {
    const scope = token.scope_type || 'account';
    const scopeLabel = scope === 'account'
      ? 'Account'
      : (scope === 'submission' ? 'Submission' : 'Skill') + ' #' + String(token.scope_resource_id || '');
    const expiryLabel = token.expires_at ? 'Expires ' + window.timeAgo(token.expires_at) : 'No expiry';
    const activityLabel = token.last_active_at ? 'Last used ' + window.timeAgo(token.last_active_at) : 'Not used yet';
    return (
    '<div class="card flex items-center gap-4 p-4">' +
      '<div class="w-10 h-10 bg-surface-2 rounded-xl flex items-center justify-center"><i class="fas fa-key text-brand-400"></i></div>' +
      '<div class="flex-1 min-w-0"><div class="flex items-center gap-2">' +
        '<span class="font-medium text-zinc-200 text-sm">' + escapeHtml(token.name) + '</span>' +
        (token.status === 'revoked' ? '<span class="status-badge" style="background:rgba(127,29,29,0.3);color:#fca5a5;border-color:rgba(220,38,38,0.3)">Revoked</span>' : '') +
      '</div><div class="text-xs text-zinc-600 mt-0.5"><span class="font-mono">' + escapeHtml(token.token_prefix || 'token') +
        '...</span> &middot; ' + escapeHtml(scopeLabel) + ' &middot; ' + escapeHtml(expiryLabel) +
        ' &middot; ' + escapeHtml(activityLabel) + ' &middot; Created ' +
        (token.created_at ? window.timeAgo(token.created_at) : 'unknown') + '</div></div>' +
      (token.status !== 'revoked' ? '<button onclick="revokeToken(' + token.id + ', this)" class="btn-ghost text-xs text-red-400 hover:text-red-300"><i class="fas fa-ban mr-1"></i>Revoke</button>' : '') +
    '</div>');
  });
}
